import { useState, type CSSProperties, type ReactNode } from 'react'
import { RichTextMenu, type RichtextField } from '@puckeditor/core'
import { useTranslation } from 'react-i18next'
import Modal from '../../../../../ui/Modal'
import Button from '../../../../../ui/Button'
import TextInput from '../../../../../ui/form/TextInput'
import { sanitizeRichText } from '../richText'

type Editor = Parameters<NonNullable<RichtextField['renderInlineMenu']>>[0]['editor']

const LinkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
)

const LinkControl = ({ editor }: { editor: Editor }) => {
    const { t } = useTranslation()
    const [href, setHref] = useState<string>()
    const isActive = editor?.isActive('link') ?? false

    const close = () => setHref(undefined)
    const apply = (value: string) => {
        const chain = editor?.chain().focus().extendMarkRange('link')
        if (value.trim()) chain?.setLink({ href: value.trim() }).run()
        else chain?.unsetLink().run()
        close()
    }

    return (
        <>
            <RichTextMenu.Control
                icon={<LinkIcon />}
                title={t('link')}
                active={isActive}
                disabled={!editor}
                onClick={() => setHref(editor?.getAttributes('link').href ?? '')}
            />
            <Modal
                open={href !== undefined}
                onClose={close}
                title={t('link')}
                zIndex={1000}
                actions={
                    <>
                        {isActive && <Button variant="destructive_secondary" onClick={() => apply('')}>{t('remove')}</Button>}
                        <Button variant="secondary" onClick={close}>{t('cancel')}</Button>
                        <Button onClick={() => apply(href ?? '')}>{t('save')}</Button>
                    </>
                }
            >
                <TextInput
                    name="href"
                    label={t('url')}
                    placeholder="https://"
                    value={href ?? ''}
                    onChange={setHref}
                />
            </Modal>
        </>
    )
}

const textMenu = (allowLists: boolean): RichtextField['renderInlineMenu'] => {
    const Menu = ({ editor }: { editor: Editor }) => (
        <RichTextMenu>
            <RichTextMenu.Group>
                <RichTextMenu.Bold />
                <RichTextMenu.Italic />
                <RichTextMenu.Underline />
                <LinkControl editor={editor} />
            </RichTextMenu.Group>
            {allowLists && (
                <RichTextMenu.Group>
                    <RichTextMenu.BulletList />
                    <RichTextMenu.OrderedList />
                </RichTextMenu.Group>
            )}
        </RichTextMenu>
    )
    return Menu
}

export const richTextField = (label: string, allowLists: boolean): RichtextField => {
    const menu = textMenu(allowLists)
    return {
        type: 'richtext',
        label,
        contentEditable: true,
        options: {
            heading: false,
            blockquote: false,
            code: false,
            codeBlock: false,
            horizontalRule: false,
            strike: false,
            textAlign: false,
            bulletList: allowLists ? {} : false,
            orderedList: allowLists ? {} : false,
            listItem: allowLists ? {} : false,
            listKeymap: allowLists ? {} : false,
            link: { openOnClick: false, autolink: false },
        },
        renderMenu: menu,
        renderInlineMenu: menu,
    }
}

interface RichTextProps {
    as: 'div' | 'h1' | 'h2' | 'h3'
    value: ReactNode
    style: CSSProperties
}

export const RichText = ({ as: Tag, value, style }: RichTextProps) => typeof value === 'string'
    ? <Tag className="pv-rich-text" style={style} dangerouslySetInnerHTML={{ __html: sanitizeRichText(value, { inline: Tag !== 'div' }) }} />
    : <Tag style={style}>{value}</Tag>
